// Card #1652 (design §6.1) — class number/name + the info-block fields,
// originally shared by the Summary family (types 1/2) and reused by the
// Ranked-list family (types 6/7) for judgesPositions. Card #1794 (2026-08-20
// pare-down) removed the Summary family (types 1/2) and Mid-Class Result
// (type 6) entirely; the surviving callers are RankedListLayout
// (classNumberName, judgesPositions) and SingleEntryLayout (classNumberName,
// testName). `classStart`/`estimatedFinish`/`totalRides`/`ring` have no live
// caller left (they were Class Summary [Simple]/type-2-only fields) but stay
// on this shared component rather than being stripped — ClassHeader itself
// was never a per-type layout component the removed types owned, so it isn't
// in scope for AC4's "deleted, not orphaned" rule the way SummaryLayout/
// StartList were. Every field is individually omittable (AC3): a key not
// present on `fields` is simply not passed here (or arrives undefined), and
// each block below renders nothing for its own missing field rather than an
// empty label. The whole header renders nothing when literally none of its
// fields are present.
//
// #1811 — `classStart` and `estimatedFinish`, when passed, were derived from
// ride `scheduledTime` server-side, so they are FVD stamps and render through
// formatFvdStampTime, NOT the show's venueTimezone. This component
// deliberately does not consume VenueTimeZoneContext: it has no genuine
// instant to render. See FVD_STAMP_TIMEZONE in common-fe/venueTime for why
// pointing these at the venue zone moves every time by three hours at WEC.
import { formatFvdStampTime } from "../utils/venueTime";
import JudgeLine from "./JudgeLine";
import "./ClassHeader.css";

// #2271 (Evin, 2026-09-04, matching the approved demo pages p4.html/p5.html):
// a bare class number is too obscure for an audience, so it is prefixed
// "CL " -- className is left bare, only the number gets it. Pulled out as
// its own pure function (not inlined in the JSX below) so it has a unit test
// that does not need to evaluate JSX: this workspace's vitest.config.js runs
// *.test.js in a bare node environment with no React/jsx-runtime wired in
// (see SingleEntryLayout.test.js's own header for the same constraint), so a
// test that called this component directly would fail on the unrelated
// "React is not defined" rather than proving or disproving the prefix.
export function classNumberNameLabel(classNumberName) {
  return [
    classNumberName?.classNumber != null ? `CL ${classNumberName.classNumber}` : null,
    classNumberName?.className,
  ]
    .filter((v) => v != null && v !== "")
    .join(" — ");
}

export default function ClassHeader({
  classNumberName,
  testName,
  judgesPositions,
  classStart,
  estimatedFinish,
  totalRides,
  ring,
}) {
  const hasNumberName =
    !!classNumberName && (classNumberName.classNumber != null || !!classNumberName.className);
  const hasJudges = Array.isArray(judgesPositions) && judgesPositions.length > 0;
  const hasTimes = !!classStart || !!estimatedFinish;
  const hasAnything =
    hasNumberName || !!testName || hasJudges || hasTimes || totalRides != null || !!ring;
  if (!hasAnything) return null;

  return (
    <div className="class-header" data-testid="class-header">
      {hasNumberName ? (
        <h1 className="class-header__number-name" data-testid="class-header-number-name">
          {classNumberNameLabel(classNumberName)}
        </h1>
      ) : null}
      {testName ? (
        <p className="class-header__test-name" data-testid="class-header-test-name">
          {testName}
        </p>
      ) : null}
      <JudgeLine judgesPositions={judgesPositions} />
      {ring ? (
        <p className="class-header__ring" data-testid="class-header-ring">
          Ring {ring}
        </p>
      ) : null}
      {hasTimes ? (
        <p className="class-header__times" data-testid="class-header-times">
          {classStart ? (
            <span data-testid="class-header-start">
              Start {formatFvdStampTime(classStart)}
            </span>
          ) : null}
          {estimatedFinish ? (
            <span data-testid="class-header-estimated-finish">
              {classStart ? " · " : ""}
              Est. finish {formatFvdStampTime(estimatedFinish)}
            </span>
          ) : null}
        </p>
      ) : null}
      {totalRides != null ? (
        <p className="class-header__total-rides" data-testid="class-header-total-rides">
          {totalRides} rides
        </p>
      ) : null}
    </div>
  );
}
